import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  Paper, makeStyles, Link, Typography, Box,
  Dialog, DialogContent, DialogActions, Button, IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import CreateQualityEventDialog from './CreateQualityEventDialog';
import { DEVIATION_DATA, mergeTabData, fmtDateTime, selectTabData } from './manufacturingTabData';
import { deviationDocs, openDoc } from './mfgDocLinks';
import Pagination from '../../logisticsDashboard/components/Pagination';


const fmtTsParts = (v) => {
  if (!v) return null;
  const m = moment(v);
  if (!m.isValid()) return null;
  return { time: m.format('HH:mm:ss'), date: m.format('DD-MMM-YYYY').toUpperCase() };
};

// "Reporting Date / Step" holds a date (or date+time) followed by " / <step>".
// Split it into its date and step parts.
const splitDateStep = (v) => {
  const s = v == null ? '' : String(v);
  const idx = s.indexOf(' / ');
  return idx === -1 ? { date: s, step: '' } : { date: s.slice(0, idx), step: s.slice(idx + 3) };
};

// Build the "Reporting Date / Step" display for a row: format the date (taken from
// the API value when present, otherwise the Excel fallback) to the shared Excel
// format, and fill the step from the API value, falling back to the Excel step when
// the API omits it. Works for already-formatted values and raw API ISO dates.
const resolveReportingDate = (apiVal, excelVal) => {
  const a = splitDateStep(apiVal);
  const e = splitDateStep(excelVal);
  return {
    date: fmtDateTime(a.date || e.date),
    step: a.step || e.step,
  };
};

const useStyles = makeStyles(() => ({
  tableContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    margin: '8px 0',
    overflowX: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  scrollArea: {
    width: '100%',
    maxHeight: 440,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  headerRow: {
    backgroundColor: '#F9F9FC',
    '& th': {
      backgroundColor: '#F9F9FC',
      fontWeight: 600,
      fontSize: '0.88rem',
      color: '#333',
      padding: '8px 10px',
      borderBottom: '1px solid #e0e0e0',
      verticalAlign: 'bottom',
      lineHeight: 1.3,
      whiteSpace: 'normal',
      // Wrap multi-word headers at spaces ("Deviation Number" -> two lines), never
      // mid-word — with content-sized columns break-word split every character.
      wordBreak: 'normal',
      overflowWrap: 'normal',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  },
  bodyRow: {
    '&:hover': { backgroundColor: '#f5f5f5' },
    '& td': {
      fontSize: '0.9rem',
      padding: '8px 10px',
      borderBottom: '1px solid #eeeeee',
      color: '#444',
      verticalAlign: 'top',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  blueLink: {
    color: '#1976d2',
    cursor: 'pointer',
    fontSize: '0.88rem',
    fontWeight: 500,
    textDecoration: 'none',
    display: 'block',
    '&:hover': { textDecoration: 'underline' },
  },
  timestamp: {
    fontSize: '0.68rem',
    color: '#888',
    display: 'block',
    marginTop: '2px',
  },
  readMore: {
    color: '#1976d2',
    cursor: 'pointer',
    fontSize: '0.88rem',
    fontWeight: 500,
  },
  subLabel: {
    fontSize: '0.82rem',
    color: '#666',
  },
  stackedCell: {
    '& > *:not(:last-child)': {
      borderBottom: '1px solid #e0e0e0',
      paddingBottom: '4px',
      marginBottom: '4px',
    },
  },
  stackedValue: {
    fontSize: '0.88rem',
    color: '#444',
    paddingBottom: '4px',
    marginBottom: '4px',
    borderBottom: '1px solid #e8e8e8',
    display: 'block',
    '&:last-child': {
      borderBottom: 'none',
      paddingBottom: 0,
      marginBottom: 0,
    },
  },
  // Stack the "Generate" / "Deviation" words vertically. A MUI Button's label is a
  // flex row by default, so a <br /> inside it doesn't break — force a column here.
  generateBtnLabel: {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    lineHeight: 1.15,
  },
}));


const TRUNCATE = 55;

const TruncatedText = ({ text, label, classes }) => {
  const [open, setOpen] = useState(false);
  if (text.length <= TRUNCATE) return <span style={{ fontSize: '0.88rem' }}>{text}</span>;
  return (
    <>
      <span style={{ fontSize: '0.88rem' }}>
        {text.substring(0, TRUNCATE)}...
        <span className={classes.readMore} onClick={() => setOpen(true)}>
          read more
        </span>
      </span>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <Box display="flex" justifyContent="space-between" alignItems="center" style={{ padding: '12px 16px 0' }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{label || 'Details'}</Typography>
          <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent>
          <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: '#444' }}>{text}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary" size="small">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Static UI text for first row — preventive action doesn't come from the API
const FIRST_ROW_PREVENTIVE_ACTION = `The filter cloth fixing checklist for Agitated Nutsche Filter Dryer (ANFD) (reference No. F-PR-113) shall be revised to incorporate the following checkpoints:
a) Verification of the straightness of the fixing plates by placing the fixing plates on the ANFD bottom dish.
b) A note point shall be incorporated as "If any abnormality is observed and rectification is performed during filter cloth fixing verification, all checkpoints shall be repeated by using a new checklist after the rectification".`;

const DEMO_PO = '1150345';
// Empty entries = no sponsor deviation generated yet → "Generate Deviation" stays
// enabled and the Sponsor Deviation Status / ID column shows "-".
const DEMO_SPONSOR_DEV_IDS = ['', 'SP-QE-2026-0004', '', 'SP-QE-2026-0007'];

// Each column takes the API value where present, otherwise falls back to the
// "Deviation" sheet of the batch's Manufacturing Collaboration workbook
// (see mergeTabData). Any other batch's fallback carries its own batch number.
const DeviationsTab = ({ data, loading, rowData, pageSize = 10 }) => {
  const classes = useStyles();
  const PAGE_SIZE = pageSize || 10;
  const rows = mergeTabData(data, DEVIATION_DATA, rowData?.batchNumber);
  // per-row: VV has one deviation/batch, Herceptin has several — resolve by row (deviationNumber)
  const devDocsFor = (row) => deviationDocs(rowData?.batchNumber, row);
  // Excel fallback rows for the batch — used to fill the reporting date/step when
  // the API row omits them (the merge overwrites reportingDate with the API value).
  const excelRows = selectTabData(DEVIATION_DATA, rowData?.batchNumber);
  const isDemoPO = rowData?.poNumber === DEMO_PO || rowData?.scheduleAgreementNumber === DEMO_PO;
  // "Generate Deviation" is a sponsor-side action (Sponsor -> CMO); hidden for the CMO.
  const isSponsor = localStorage.getItem('collabType') !== 'CMO';

  // Create Quality Event flow: which row opened the form, and any sponsor QE ids
  // generated during this session (keyed by row index).
  const [qeDialog, setQeDialog] = useState({ open: false, deviation: null, idx: null });
  const [generatedMap, setGeneratedMap] = useState({});

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage, PAGE_SIZE]
  );

  const handleGenerateClick = (row, idx) => {
    setQeDialog({ open: true, deviation: row, idx });
  };

  const handleQeSubmitted = (result) => {
    const idx = qeDialog.idx;
    if (idx !== null && idx !== undefined) {
      setGeneratedMap((prev) => ({ ...prev, [idx]: result }));
    }
  };

  if (loading) return <Box style={{ textAlign: 'center', padding: 24 }}>Loading...</Box>;

  return (
    <Paper className={classes.tableContainer} elevation={0}>
      <div className={classes.scrollArea}>
        <Table stickyHeader size="small" style={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell>Deviation<br />Number</TableCell>
              <TableCell>Reporting<br />Date / Step</TableCell>
              <TableCell style={{ minWidth: '120px' }}>Initial Deviation<br />Description</TableCell>
              <TableCell style={{ minWidth: '150px' }}>Deviation<br />Assessment<br />Report</TableCell>
              <TableCell style={{ minWidth: '150px' }}>Final Deviation<br />Report</TableCell>
              <TableCell style={{ minWidth: '140px' }}>Corrective<br />Action</TableCell>
              <TableCell style={{ minWidth: '140px' }}>Preventive<br />Action</TableCell>
              <TableCell style={{ minWidth: '140px' }}>
                Deviation Status<br />
                Deviation Category<br />
                Deviation Type
              </TableCell>
              <TableCell>Informed<br />Sponsor</TableCell>
              {isSponsor && <TableCell style={{ width: '120px', textAlign:'center' }}>Generate<br />Deviation</TableCell>}
              <TableCell style={{ minWidth: '140px' }}>
                Sponsor<br />Deviation Status<br />
                Deviation ID
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={isSponsor ? 11 : 10} style={{ textAlign: 'center' }}>No data available</TableCell></TableRow>
            ) : pageRows.map((row, i) => {
              const idx = (safePage - 1) * PAGE_SIZE + i;
              const generated = generatedMap[idx];
              const sponsorDevId = generated?.sponsorDeviationId
                || ((isDemoPO && DEMO_SPONSOR_DEV_IDS[idx]) ? DEMO_SPONSOR_DEV_IDS[idx] : (row.sponsorDeviationId || ''));
              const hasSponsorDev = !!sponsorDevId; // sponsor deviation already generated → disable button
              const sponsorDevStatus = generated?.sponsorDeviationStatus || row.sponsorDeviationStatus || 'Closed';
              return (
              <TableRow key={idx} className={classes.bodyRow}>
                {/* Deviation Number */}
                <TableCell style={{ fontWeight: 500 }}>{row.deviationNumber}</TableCell>

                {/* Reporting Date / Step — API date if present else Excel date;
                    step falls back to the Excel value when the API omits it. */}
                <TableCell>
                  {(() => {
                    const reporting = resolveReportingDate(
                      row.reportingDate,
                      (excelRows[idx] || excelRows[0])?.reportingDate,
                    );
                    return (
                      <>
                        <span style={{ display: 'block' }}>{reporting.date} / </span>
                        {reporting.step && (
                          <span className={classes.subLabel} style={{ display: 'block', marginTop: 2 }}>
                            {reporting.step}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </TableCell>

                {/* Initial Description */}
                <TableCell>
                  <TruncatedText text={row.initialDescription || ''} label="Initial Deviation Description" classes={classes} />
                </TableCell>

                {/* Assessment Report */}
                <TableCell>
                  <Link className={classes.blueLink} style={{ cursor: 'pointer' }} onClick={() => openDoc(devDocsFor(row).assessment)}>
                    {row.assessmentReport?.name || "QE-2026-005.Deviation Report.pdf"}
                  </Link>
                  {(() => {
                    const ts = fmtTsParts(row.assessmentReport?.timestamp);
                    return true ? (
                      <>
                        <span className={classes.timestamp}>Timestamp: {ts?.time || "11:42:00"}</span>
                        <span className={classes.timestamp}>{ts?.date || "24-JAN-2026"}</span>
                      </>
                    ) : null;
                  })()}
                </TableCell>

                {/* Final Deviation Report */}
                <TableCell>
                  <Link className={classes.blueLink} style={{ cursor: 'pointer' }} onClick={() => openDoc(devDocsFor(row).final)}>
                    {row.finalReport?.name || "QE-2026-005.Final Deviation Report.pdf"}
                  </Link>
                  {(() => {
                    const ts = fmtTsParts(row.finalReport?.timestamp);
                    return true ? (
                      <>
                        <span className={classes.timestamp}>Timestamp: {ts?.time || "11:42:00"}</span>
                        <span className={classes.timestamp}>{ts?.date || "24-JAN-2026"}</span>
                      </>
                    ) : null;
                  })()}
                  {row.finalReport?.note && (
                    <span style={{ display: 'block', marginTop: 6, color: '#444' }}>
                      <TruncatedText text={row.finalReport.note} label="Final Deviation Report" classes={classes} />
                    </span>
                  )}
                </TableCell>

                {/* Corrective Action + CMO CA Status */}
                <TableCell>
                  <TruncatedText text={row.correctiveAction?.text || ''} label="Corrective Action" classes={classes} />
                  <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginTop: '4px' }}>
                    CMO CA Status:<br />{row.cmoCAStatus || row.correctiveAction?.caStatus}
                  </span>
                </TableCell>

                {/* Preventive Action + CMO PA Status */}
                <TableCell>
                  <TruncatedText text={(isDemoPO && idx === 0) ? FIRST_ROW_PREVENTIVE_ACTION : (row.preventiveAction?.text || '')} label="Preventive Action" classes={classes} />
                  <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginTop: '4px' }}>
                    CMO PA Status:<br />{(isDemoPO && idx === 0) ? 'Closed' : (row.cmoPAStatus || row.preventiveAction?.paStatus)}
                  </span>
                </TableCell>

                {/* Deviation Status / Category / Type stacked */}
                <TableCell>
                  <span className={classes.stackedValue}>{row.deviationStatus}</span>
                  <span className={classes.stackedValue}>{row.deviationCategory}</span>
                  <span className={classes.stackedValue}>{row.deviationType}</span>
                </TableCell>

                {/* Informed Sponsor */}
                <TableCell>{row.informedSponsor}</TableCell>

                {/* Generate Deviation — sponsor only. Disabled once a sponsor QE
                    exists for the row; otherwise opens the Create Quality Event form. */}
                {isSponsor && (
                  <TableCell style={{ width: '120px', verticalAlign: 'middle' }} align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      // disabled={hasSponsorDev}
                      onClick={() => handleGenerateClick(row, idx)}
                      classes={{ label: classes.generateBtnLabel }}
                      style={{ borderRadius: 20, textTransform: 'none', fontSize: '0.78rem', padding: '3px 10px', minWidth: 0, lineHeight: 1.15 }}
                    >
                      <span>Generate</span>
                      <span>Deviation</span>
                    </Button>
                  </TableCell>
                )}

                {/* Sponsor Deviation Status / Deviation ID stacked, or "-" when not yet generated */}
                <TableCell>
                  {hasSponsorDev ? (
                    <>
                      <span className={classes.stackedValue}>{sponsorDevStatus}</span>
                      <span className={classes.stackedValue}>{sponsorDevId}</span>
                    </>
                  ) : '-'}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {rows.length > PAGE_SIZE && (
        <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
      )}

      <CreateQualityEventDialog
        open={qeDialog.open}
        deviation={qeDialog.deviation}
        onClose={() => setQeDialog({ open: false, deviation: null, idx: null })}
        onSubmitted={handleQeSubmitted}
      />
    </Paper>
  );
};

export default DeviationsTab;
